import { useState, useRef, useCallback, useEffect } from 'react';
import type { Swiper as SwiperType } from 'swiper';

export function useVideoPlayer(imageId: string | undefined, imageType: string | undefined) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(() => window.innerWidth < 768);
  const [buffered, setBuffered] = useState(0);
  const [showBigPlay, setShowBigPlay] = useState(true);

  const videoRef = useRef<HTMLVideoElement>(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const prevImageIdRef = useRef<string | null>(null);

  // Helper: pause ALL videos everywhere (desktop ref + swiper)
  const pauseAllVideos = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    if (swiperRef.current?.el) {
      const allVideos = swiperRef.current.el.querySelectorAll('video');
      allVideos.forEach((video: HTMLVideoElement) => {
        video.pause();
        video.currentTime = 0;
      });
    }
  }, []);

  useEffect(() => {
    if (!imageId) return;

    const isNewMedia = prevImageIdRef.current !== imageId;

    if (isNewMedia) {
      pauseAllVideos();
    }

    prevImageIdRef.current = imageId;

    if (!isNewMedia) return;

    // Reset all video state
    setCurrentTime(0);
    setDuration(0);
    setBuffered(0);
    setIsPlaying(false);
    setShowBigPlay(false);

    if (imageType === 'video') {
      const timer = setTimeout(() => {
        pauseAllVideos();

        const isMobile = window.innerWidth < 768;
        const video = videoRef.current;
        if (video && !isMobile) {
          video.load();

          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {
              video.muted = true;
              setIsMuted(true);
              video.play().catch(() => {
                setShowBigPlay(true);
                setIsPlaying(false);
              });
            });
          }
        }

        if (isMobile) {
          setShowBigPlay(true);
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [imageId, imageType, pauseAllVideos]);

  useEffect(() => {
    return () => { pauseAllVideos(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVideoTimeUpdate = useCallback(() => {
    if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
  }, []);

  const handleVideoLoadedMetadata = useCallback(() => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  }, []);

  const handleVideoProgress = useCallback(() => {
    if (videoRef.current && videoRef.current.buffered.length > 0) {
      const bufferedEnd = videoRef.current.buffered.end(videoRef.current.buffered.length - 1);
      setBuffered((bufferedEnd / videoRef.current.duration) * 100);
    }
  }, []);

  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
    setShowBigPlay(false);
  }, []);

  const handleVideoPause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const togglePlay = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  }, [isPlaying]);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = pos * duration;
    }
  }, [duration]);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  }, []);

  const toggleMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isMuted) {
        videoRef.current.volume = volume || 1;
        videoRef.current.muted = false;
        setIsMuted(false);
      } else {
        videoRef.current.muted = true;
        setIsMuted(true);
      }
    }
  }, [isMuted, volume]);

  const toggleFullscreen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  }, []);

  const formatTime = useCallback((time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  return {
    videoRef,
    swiperRef,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    buffered,
    showBigPlay,
    pauseAllVideos,
    handleVideoTimeUpdate,
    handleVideoLoadedMetadata,
    handleVideoProgress,
    handleVideoPlay,
    handleVideoPause,
    togglePlay,
    handleProgressClick,
    handleVolumeChange,
    toggleMute,
    toggleFullscreen,
    formatTime,
  };
}
